import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DatabaseService } from '../services/DatabaseService';

const Dashboard = () => {
  const [userPolls, setUserPolls] = useState([]);
  const [participatedPolls, setParticipatedPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const { currentUser } = useAuth();
  
  useEffect(() => {
    const fetchPolls = async () => {
      try {
        // Fetch user's created polls
        const polls = await DatabaseService.getUserPolls(currentUser.uid);
        
        // Format the polls to ensure proper date display and participant count
        const formattedPolls = polls.map(poll => {
          // Handle date formatting
          let formattedDate = 'Unknown Date';
          if (poll.createdAt) {
            // Handle both Firestore timestamps and ISO strings
            const timestamp = typeof poll.createdAt === 'object' && poll.createdAt.toDate 
              ? poll.createdAt.toDate() 
              : new Date(poll.createdAt);
              
            if (!isNaN(timestamp)) {
              formattedDate = timestamp.toLocaleDateString();
            }
          }
          
          // Handle participant count
          let participantCount = 0;
          if (Array.isArray(poll.participantIds)) {
            participantCount = poll.participantIds.length;
          } else if (Array.isArray(poll.participants)) {
            participantCount = poll.participants.length;
          }
          
          return {
            ...poll,
            formattedDate,
            participantCount
          };
        });
        
        setUserPolls(formattedPolls);
        
        // Fetch polls the user has participated in
        const participated = await DatabaseService.getUserParticipatedPolls(currentUser.uid);
        
        // Format these polls as well
        const formattedParticipated = participated.map(poll => {
          // Handle date formatting
          let formattedDate = 'Unknown Date';
          if (poll.createdAt) {
            // Handle both Firestore timestamps and ISO strings
            const timestamp = typeof poll.createdAt === 'object' && poll.createdAt.toDate 
              ? poll.createdAt.toDate() 
              : new Date(poll.createdAt);
              
            if (!isNaN(timestamp)) {
              formattedDate = timestamp.toLocaleDateString();
            }
          }
          
          // Handle participant count
          let participantCount = 0;
          if (Array.isArray(poll.participantIds)) {
            participantCount = poll.participantIds.length;
          } else if (Array.isArray(poll.participants)) {
            participantCount = poll.participants.length;
          }
          
          return {
            ...poll,
            formattedDate,
            participantCount
          };
        });
        
        setParticipatedPolls(formattedParticipated);
      } catch (err) {
        console.error('Failed to load polls:', err);
        setError('Failed to load polls: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPolls();
  }, [currentUser]);
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="md:flex md:justify-between md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome back, {currentUser.displayName || currentUser.email}
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <Link
            to="/polls/create"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Create New Poll
          </Link>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
      
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : (
        <div className="grid gap-6 mb-8 md:grid-cols-2">
          {/* Your Polls Section */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <h2 className="text-lg leading-6 font-medium text-gray-900">Your Polls</h2>
              <p className="mt-1 text-sm text-gray-500">Polls you have created</p>
            </div>
            <ul className="divide-y divide-gray-200">
              {userPolls.length === 0 ? (
                <li className="px-4 py-6 text-center text-gray-500">
                  <p>You haven't created any polls yet.</p>
                  <Link to="/polls/create" className="mt-2 inline-flex items-center text-sm text-indigo-600 hover:text-indigo-900">
                    Create your first poll
                    <svg className="ml-1 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </Link>
                </li>
              ) : (
                userPolls.map((poll) => (
                  <li key={poll.id}>
                    <Link to={`/polls/${poll.id}`} className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <div className="truncate">
                            <div className="flex text-sm">
                              <p className="font-medium text-indigo-600 truncate">{poll.title}</p>
                              <p className="ml-1 flex-shrink-0 font-normal text-gray-500">
                                {poll.category && `in ${poll.category}`}
                              </p>
                            </div>
                            <div className="mt-2 flex">
                              <div className="flex items-center text-sm text-gray-500">
                                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                </svg>
                                <span>Created: {poll.formattedDate}</span>
                              </div>
                              <div className="ml-4 flex items-center text-sm text-gray-500">
                                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                                </svg>
                                <span>
                                  {poll.participantCount} {poll.participantCount === 1 ? 'participant' : 'participants'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="ml-5 flex-shrink-0">
                            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))
              )}
            </ul>
            {userPolls.length > 0 && (
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <Link
                  to="/polls"
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-900"
                >
                  View all your polls
                  <span aria-hidden="true"> &rarr;</span>
                </Link>
              </div>
            )}
          </div>
          
          {/* Participated Polls Section */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <h2 className="text-lg leading-6 font-medium text-gray-900">Participated Polls</h2>
              <p className="mt-1 text-sm text-gray-500">Polls you have voted in</p>
            </div>
            <ul className="divide-y divide-gray-200">
              {participatedPolls.length === 0 ? (
                <li className="px-4 py-6 text-center text-gray-500">
                  <p>You haven't participated in any polls yet.</p>
                </li>
              ) : (
                participatedPolls.map((poll) => (
                  <li key={poll.id}>
                    <Link to={`/polls/${poll.id}`} className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <div className="truncate">
                            <div className="flex text-sm">
                              <p className="font-medium text-indigo-600 truncate">{poll.title}</p>
                              <p className="ml-1 flex-shrink-0 font-normal text-gray-500">
                                {poll.category && `in ${poll.category}`}
                              </p>
                            </div>
                            <div className="mt-2 flex">
                              <div className="flex items-center text-sm text-gray-500">
                                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                                </svg>
                                <span>By: {poll.createdBy === currentUser.uid ? 'You' : 'Another user'}</span>
                              </div>
                              <div className="ml-4 flex items-center text-sm text-gray-500">
                                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                                <span>
                                  {poll.expiresAt && new Date(poll.expiresAt) > new Date() ? 'Active' : 'Closed'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="ml-5 flex-shrink-0">
                            <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}
      
      {/* Stats Section */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Your Activity</h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Polls Created</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{userPolls.length}</div>
                  </dd>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 truncate">Polls Participated In</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{participatedPolls.length}</div>
                  </dd>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                  <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dt className="text-sm font-medium text-gray-500 truncate">Active Polls</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">
                      {userPolls.filter(poll => poll.isActive).length}
                    </div>
                  </dd>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;